<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-4 col-sm-6">
          <h5>
            <router-link to="/devices" class="text-dark"><i class="fa fa-arrow-left mr-2"></i></router-link>
            Device Users Overview
          </h5>
        </div>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="row mb-4">
      <div class="col-md-3 col-6 mb-2">
        <div class="card">
          <div class="card-body text-center py-3">
            <div class="text-muted mb-1">Total Students</div>
            <div class="font-size-24 font-weight-bold text-primary">
              <a-spin v-if="loading" size="small" />
              <span v-else>{{ summary.total_users }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-2">
        <div class="card">
          <div class="card-body text-center py-3">
            <div class="text-muted mb-1">In All Devices</div>
            <div class="font-size-24 font-weight-bold text-success">
              <a-spin v-if="loading" size="small" />
              <span v-else>{{ summary.enrolled_in_all_devices }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-2">
        <div class="card">
          <div class="card-body text-center py-3">
            <div class="text-muted mb-1">In Some Devices</div>
            <div class="font-size-24 font-weight-bold text-warning">
              <a-spin v-if="loading" size="small" />
              <span v-else>{{ summary.enrolled_in_some_devices }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-6 mb-2">
        <div class="card">
          <div class="card-body text-center py-3">
            <div class="text-muted mb-1">Not In Any Device</div>
            <div class="font-size-24 font-weight-bold text-danger">
              <a-spin v-if="loading" size="small" />
              <span v-else>{{ summary.not_enrolled_in_any_device }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="row justify-content-center">
      <div class="col-xl-12">
        <div class="row align-items-center mb-2">
          <!-- Session -->
          <div class="col-md-2 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedSession"
              optionFilterProp="children"
              style="width: 100%"
              @change="handleSessionChange"
              :filterOption="false"
            >
              <template v-for="session in sessions">
                <a-select-option :value="session.id" :key="session.id">
                  {{ session.name }}
                  <small class="text-primary ml-2">{{ currentSession && currentSession.id === session.id ? 'Current' : '' }}</small>
                </a-select-option>
              </template>
            </a-select>
          </div>
          <!-- Class -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="classSpinning">
              <a-select
                size="large"
                v-model="selectedClass"
                placeholder="All Classes"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleClassSelect"
                :filterOption="false"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined">All Classes</a-select-option>
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option :value="schoolClass.id" :key="schoolClass.id">
                    {{ schoolClass.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Section -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="sectionSpinning">
              <a-select
                size="large"
                v-model="selectedSection"
                placeholder="All Sections"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleSectionChange"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="undefined">All Sections</a-select-option>
                <template v-for="section in sections">
                  <a-select-option :value="section.id" :key="section.id">
                    {{ section.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Search -->
          <div class="col-md-6 col-12 mb-2">
            <div class="text-right">
              <a-input-search
                placeholder="Search by Name or Student Code..."
                size="large"
                v-model="search"
                @search="searchUsers"
                @change="searchUsers"
              />
            </div>
          </div>
        </div>

        <!-- Data Table -->
        <div class="card">
          <div class="card-body">
            <a-table
              :columns="columns"
              :dataSource="data"
              :loading="loading"
              :pagination="false"
              :rowKey="record => record.student_id"
              :scroll="{ x: 900 }"
              size="middle"
            >
              <template slot="name" slot-scope="text, record">
                <span>{{ record.first_name }} {{ record.last_name }}</span>
              </template>

              <template slot="devices" slot-scope="text, record">
                <template v-if="record.devices.length > 0">
                  <a-tooltip v-for="(device, idx) in record.devices" :key="idx" :title="device.data_types.join(', ')">
                    <a-tag color="green" class="mb-1">
                      {{ device.device_name }}
                      <small>({{ device.data_types.join(', ') }})</small>
                    </a-tag>
                  </a-tooltip>
                </template>
                <span v-else class="text-muted">—</span>
              </template>

              <template slot="missingDevices" slot-scope="text, record">
                <template v-if="record.missing_devices.length > 0">
                  <a-tag v-for="(device, idx) in record.missing_devices" :key="idx" color="red" class="mb-1">
                    {{ device.device_name }}
                  </a-tag>
                </template>
                <span v-else class="text-success"><i class="fa fa-check"></i> All</span>
              </template>

              <template slot="coverage" slot-scope="text, record">
                <a-progress
                  :percent="record.total_device_count > 0 ? Math.round((record.enrolled_device_count / record.total_device_count) * 100) : 0"
                  :status="record.enrolled_device_count === record.total_device_count ? 'success' : 'active'"
                  size="small"
                />
                <small class="text-muted">{{ record.enrolled_device_count }}/{{ record.total_device_count }}</small>
              </template>
            </a-table>
          </div>
        </div>

        <!-- Pagination -->
        <div class="text-right">
          <a-pagination
            showSizeChanger
            @showSizeChange="onShowSizeChange"
            :total="meta.total"
            :pageSize.sync="pageSize"
            v-model="current"
            @change="onChange"
          >
          </a-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, find } from 'lodash'

export default {
  name: 'DeviceUsersOverview',
  mounted() {
    this.$http.get('/api/session')
      .then(({ data }) => {
        this.sessions = data.data
        this.currentSession = find(data.data, ['is_current', 1])
        if (this.currentSession) {
          this.selectedSession = this.currentSession.id
        }
        this.getClasses()
        this.fetchData()
      })
  },
  created() {
    this.searchUsers = debounce(this.searchUsers, 500)
  },
  data() {
    return {
      loading: false,
      data: [],
      meta: { total: 0, per_page: 30, current_page: 1 },
      summary: {
        total_users: 0,
        enrolled_in_all_devices: 0,
        enrolled_in_some_devices: 0,
        not_enrolled_in_any_device: 0,
        total_devices: 0,
      },
      sessions: [],
      currentSession: null,
      schoolClasses: [],
      sections: [],
      selectedSession: undefined,
      selectedClass: undefined,
      selectedSection: undefined,
      classSpinning: false,
      sectionSpinning: false,
      search: null,
      current: 1,
      pageSize: 30,
      columns: [
        {
          title: 'Student Code',
          dataIndex: 'student_code',
          key: 'student_code',
          width: 120,
        },
        {
          title: 'Name',
          key: 'name',
          scopedSlots: { customRender: 'name' },
          width: 180,
        },
        {
          title: 'Class / Section',
          dataIndex: 'class_section',
          key: 'class_section',
          width: 140,
        },
        {
          title: 'Enrolled Devices',
          key: 'devices',
          scopedSlots: { customRender: 'devices' },
        },
        {
          title: 'Missing Devices',
          key: 'missing_devices',
          scopedSlots: { customRender: 'missingDevices' },
        },
        {
          title: 'Coverage',
          key: 'coverage',
          scopedSlots: { customRender: 'coverage' },
          width: 150,
        },
      ],
    }
  },
  methods: {
    fetchData(isSearch) {
      this.loading = true

      let page = this.current
      if (isSearch) {
        page = 1
        this.current = 1
      }

      this.$http.get('/api/devices/users-overview', {
        params: {
          session_id: this.selectedSession,
          class_id: this.selectedClass,
          section_id: this.selectedSection,
          search: this.search,
          limit: this.pageSize,
          page: page,
        },
      }).then(({ data }) => {
        this.data = data.data
        this.meta = data.meta
        this.summary = data.summary
      }).catch(() => {
        this.$notification.error({ message: 'Error', description: 'Failed to fetch users overview' })
      }).finally(() => {
        this.loading = false
      })
    },
    searchUsers(e) {
      if (e && e.target) {
        this.search = e.target.value
      }
      this.fetchData(true)
    },
    handleSessionChange(value) {
      this.selectedSession = value
      this.selectedSection = undefined
      this.selectedClass = undefined
      this.getClasses()
      this.fetchData(true)
    },
    getClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', {
        params: { session_id: this.selectedSession },
      }).then(({ data }) => {
        this.schoolClasses = data.data
        this.classSpinning = false
      })
    },
    handleClassSelect(value) {
      this.selectedSection = undefined
      if (value) {
        this.sectionSpinning = true
        this.getClassSections(value)
      }
      this.fetchData(true)
    },
    getClassSections(value) {
      this.$http.get(`/api/section/class/${value}`, {
        params: { session_id: this.selectedSession },
      }).then(({ data }) => {
        this.sections = data.data
        this.sectionSpinning = false
      })
    },
    handleSectionChange(value) {
      this.selectedSection = value || undefined
      this.fetchData(true)
    },
    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.current = current
      this.fetchData()
    },
    onChange(current) {
      this.current = current
      this.fetchData()
    },
  },
}
</script>
<style scoped>
.font-size-24 {
  font-size: 24px;
}
</style>
